import type { Entry, Ergebnis } from '@baba/engine/types';
import {
  ART_FARBE,
  ART_TITEL,
  datum,
  euro,
  postenTitel,
} from '../format.ts';

interface Props {
  entries: Entry[];
  ergebnis: Ergebnis;
  aufDetail: (id: string) => void;
  /**
   * Filter und Suche liegen bewusst in App und nicht hier.
   *
   * Beim Öffnen eines Eintrags steigt App früh in den Detail-Zweig aus und
   * hängt diese Ansicht aus - lokaler Zustand wäre danach weg. Wer den
   * Papierkorb durchsieht und einen Eintrag wiederherstellt, stand vorher
   * wieder bei "Alle" und ohne Suchbegriff.
   */
  filter: Filter;
  aufFilter: (f: Filter) => void;
  suche: string;
  aufSuche: (s: string) => void;
}

export type Filter = 'alle' | Entry['type'] | 'papierkorb';

const FILTER: { wert: Filter; titel: string }[] = [
  { wert: 'alle', titel: 'Alle' },
  { wert: 'trip', titel: 'Ausflug' },
  { wert: 'refuel', titel: 'Tanken' },
  { wert: 'repair', titel: 'Reparatur' },
  { wert: 'settlement', titel: 'Ausgleich' },
  { wert: 'fixed_cost', titel: 'Fixkosten' },
  { wert: 'papierkorb', titel: 'Papierkorb' },
];

function betragVon(e: Entry, ergebnis: Ergebnis): string {
  switch (e.type) {
    case 'trip': {
      // Ein laufender Ausflug hat noch keinen Preis - "0,00 €" wäre eine
      // Behauptung, die sich beim Stoppen als falsch herausstellt (R-23).
      if (e.status === 'laufend') return 'läuft';
      const r = ergebnis.trips.get(e.id);
      return r ? euro(r.preisAusflug) : '';
    }
    default:
      return euro(e.betrag);
  }
}

/**
 * Freitextsuche über Beschreibung, Namen der MitfahrerInnen, Vorgangsart,
 * Datum und Betrag. Ein Vorgang passt, wenn jedes Suchwort irgendwo vorkommt -
 * so findet "otto tanken" die Tankvorgänge von Otto.
 */
function passtZurSuche(e: Entry, ergebnis: Ergebnis, suche: string): boolean {
  const woerter = suche.toLowerCase().split(/\s+/).filter(Boolean);
  if (woerter.length === 0) return true;

  const teile = [
    ART_TITEL[e.type],
    postenTitel(e),
    e.beschreibung ?? '',
    datum(e.datum),
    e.datum,
    betragVon(e, ergebnis),
  ];
  if (e.type === 'trip') teile.push(e.namenMitfahrer ?? '');

  const heuhaufen = teile.join(' ').toLowerCase();
  return woerter.every((w) => heuhaufen.includes(w));
}

export function Historie({
  entries,
  ergebnis,
  aufDetail,
  filter,
  aufFilter,
  suche,
  aufSuche,
}: Props) {

  const gefiltert = entries
    .filter((e) =>
      filter === 'papierkorb'
        ? e.geloescht
        : !e.geloescht && (filter === 'alle' || e.type === filter),
    )
    .filter((e) => passtZurSuche(e, ergebnis, suche))
    .sort((a, b) =>
      a.datum !== b.datum ? (a.datum < b.datum ? 1 : -1) : b.seq - a.seq,
    );

  let letztesJahr = '';

  return (
    <>
      <div className="suchfeld">
        <input
          type="search"
          inputMode="search"
          placeholder="Suchen"
          value={suche}
          onChange={(e) => aufSuche(e.target.value)}
          aria-label="Vorgänge durchsuchen"
        />
        {suche !== '' && (
          <button
            className="suchfeld__leeren"
            onClick={() => aufSuche('')}
            aria-label="Suche zurücksetzen"
          >
            ×
          </button>
        )}
      </div>

      <div className="filterband">
        {FILTER.map((f) => (
          <button
            key={f.wert}
            className="chip"
            aria-pressed={filter === f.wert}
            onClick={() => aufFilter(f.wert)}
          >
            {f.titel}
          </button>
        ))}
      </div>

      {gefiltert.length === 0 ? (
        <p className="leer">
          {suche !== ''
            ? `Nichts gefunden zu „${suche}".`
            : filter === 'papierkorb'
              ? 'Der Papierkorb ist leer.'
              : 'Noch nichts eingetragen. Mit dem Plus unten rechts geht es los.'}
        </p>
      ) : (
        <ul className="liste">
          {gefiltert.map((e) => {
            const jahr = e.datum.slice(0, 4);
            const neuesJahr = jahr !== letztesJahr;
            letztesJahr = jahr;
            const abgeschlossen = e.type === 'trip' && e.finalisiert;

            return (
              <li key={e.id}>
                {neuesJahr && <p className="jahrestrenner">{jahr}</p>}
                <button
                  className={
                    e.geloescht ? 'posten posten--geloescht' : 'posten'
                  }
                  onClick={() => aufDetail(e.id)}
                >
                  <span
                    className="posten__marke"
                    style={{ background: ART_FARBE[e.type] }}
                    aria-hidden="true"
                  />
                  <span className="posten__text">
                    <span className="posten__kopf">
                      {datum(e.datum)} · {ART_TITEL[e.type]}
                      {abgeschlossen && ' · abgeschlossen'}
                    </span>
                    <span className="posten__titel">{postenTitel(e)}</span>
                  </span>
                  <span className="posten__betrag">
                    {betragVon(e, ergebnis)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
